import { z } from "zod";

import { filedAmountSchema } from "./document-filing";
import type { FinanceAuditEvent } from "./reconciliation";
import type { RevolutPayments } from "./revolut";

// Paying a bill with Revolut: an editor of Finance sends a vendor's bill to Revolut as a draft payment, to the bank
// account Ravnar keeps for the vendor (`payees.ts`), and the business's owner approves — or deletes — it in Revolut.
// Ravnar never moves money itself; the bill is paid once reconciliation matches the transfer to it. The API and the
// frontend share these.

/**
 * Where a bill's draft payment stands, as Ravnar last heard it from Revolut:
 *
 * - `sending` — kept before Revolut was asked, which has not answered yet, or not in words Ravnar could read: sending
 *   it again, with the same idempotency key, makes no second draft while Revolut keeps the key — so it is sent again
 *   only within 23 hours of its first sending (`canResendBillPayment`);
 * - `awaiting` — the draft waits in Revolut for the business's owner to approve it;
 * - `approved` — the owner approved it, and Revolut is making the transfer;
 * - `completed` — Revolut made the transfer;
 * - `failed` — Revolut refused the draft, or the transfer failed, for the reason it gave;
 * - `deleted` — the draft was deleted, in Ravnar or in Revolut.
 */
export const BILL_PAYMENT_STATUSES = ["sending", "awaiting", "approved", "completed", "failed", "deleted"] as const;
export type BillPaymentStatus = (typeof BILL_PAYMENT_STATUSES)[number];

export const BILL_PAYMENT_STATUS_LABELS: Readonly<Record<BillPaymentStatus, string>> = {
	sending: "Sending",
	awaiting: "Waiting for approval",
	approved: "Approved",
	completed: "Transferred",
	failed: "Failed",
	deleted: "Deleted",
};

/** Whether a bill whose draft stands so may be sent to Revolut again: nothing of that draft can move money any more. */
export function canSendBillAgain(status: BillPaymentStatus): status is "failed" | "deleted" {
	return status === "failed" || status === "deleted";
}

/**
 * How long after a draft was last sent a sending of it may still be on its way: longer than any route may run (ten
 * seconds), so a draft is neither sent again nor forgotten while a sending of it may still be answered.
 */
export const BILL_PAYMENT_SENDING_WINDOW_MS = 60_000;

/**
 * How long after its first sending a draft being sent may be sent again with its idempotency key. Revolut keeps a key
 * for 24 hours on its other payment endpoints and says nothing of the drafts', so a little less than that: past it, a
 * sending again could make a second draft.
 */
export const BILL_PAYMENT_RESEND_LIMIT_MS = 23 * 60 * 60 * 1000;

/** What tells whether a draft may be sent again, or forgotten, at a given moment. */
type TimedBillPayment = Pick<BillPayment, "status" | "sentAt" | "lastSentAt">;

/** Whether a sending of a draft may still be on its way at `now`: it is then neither sent again nor forgotten. */
export function isBillPaymentInFlight(payment: TimedBillPayment, now: number): boolean {
	return payment.status === "sending" && now - Date.parse(payment.lastSentAt) < BILL_PAYMENT_SENDING_WINDOW_MS;
}

/**
 * Whether a draft being sent may be sent again as it was, with its key, at `now`: no sending of it is on its way, and
 * its first sending is less than 23 hours old.
 */
export function canResendBillPayment(payment: TimedBillPayment, now: number): boolean {
	return (
		payment.status === "sending" &&
		!isBillPaymentInFlight(payment, now) &&
		now - Date.parse(payment.sentAt) < BILL_PAYMENT_RESEND_LIMIT_MS
	);
}

/**
 * Whether a draft may be forgotten at `now` — Ravnar's record of it removed, without a word to Revolut — as one Ravnar
 * cannot follow: Revolut never said whether it made it (`sending`, once no sending of it is on its way), or it waits or
 * was approved under payments Ravnar can no longer ask Revolut with (`followed` false: stopped, refused, or allowed anew
 * since). A forgotten draft that Revolut made still waits there for the business's owner, so whoever forgets one looks
 * in Revolut first.
 */
export function canForgetBillPayment(payment: TimedBillPayment, followed: boolean, now: number): boolean {
	const { status } = payment;
	return (
		(status === "sending" && !isBillPaymentInFlight(payment, now)) ||
		((status === "awaiting" || status === "approved") && !followed)
	);
}

/** The statuses of a draft that may still be paid through Revolut. */
const UNSETTLEABLE_STATUSES = ["sending", "awaiting", "approved"] as const;
type UnsettleableStatus = (typeof UNSETTLEABLE_STATUSES)[number];

/**
 * Whether a bill whose draft stands so is kept from being settled by hand: being sent, waiting or approved, it may
 * still be paid through Revolut. A transferred one is not: settling it moves no money, and is the way to close a bill
 * whose transfer never reaches the ledger.
 */
export function blocksSettlingBill(status: BillPaymentStatus): status is UnsettleableStatus {
	return (UNSETTLEABLE_STATUSES as readonly BillPaymentStatus[]).includes(status);
}

/**
 * Why a bill whose draft stands so is not settled by hand — settling it while the draft may still be paid would pay it
 * twice — as the settle route refuses it and the bill's drawer says it.
 */
export const BILL_PAYMENT_SETTLE_REFUSALS: Readonly<Record<UnsettleableStatus, string>> = {
	sending:
		"A draft of this bill is being sent to Revolut. Send it again, or forget it, under Pay with Revolut first.",
	awaiting: "A draft of this bill waits in Revolut. Delete it, or forget it, under Pay with Revolut first.",
	approved:
		"The owner approved this bill's draft in Revolut, which is making the transfer. The bill is paid once reconciliation matches it.",
};

/** What the bill's drawer says above Settle by hand once Revolut made the bill's transfer. */
export const BILL_PAYMENT_TRANSFERRED_NOTE =
	"Revolut made this bill's transfer: reconciliation marks the bill paid once the bank sync reads it. Settle it by hand only if it never does.";

/**
 * What the bill's drawer says when the bill is paid or settled while a draft of it may still be paid, by where the draft
 * stands: what may be done about it differs.
 */
export const BILL_PAYMENT_PAID_WARNINGS: Readonly<Record<UnsettleableStatus, string>> = {
	sending:
		"This bill is paid or settled, but a draft of it may have reached Revolut: look for it in Revolut Business's drafts and delete it there, then forget it here.",
	awaiting:
		"This bill is paid or settled, but its draft still waits in Revolut: delete it, here or in Revolut, so the owner does not pay it twice.",
	approved:
		"This bill is paid or settled, and the owner approved its draft in Revolut: unless that transfer is what paid it, check Revolut Business's transfers so it is not paid twice.",
};

/** A payment reference as Revolut takes one: at most 100 characters. */
export const BILL_PAYMENT_REFERENCE_MAX_LENGTH = 100;

/**
 * A bill's draft payment, as the API answers it: to whom, how much, with what reference, where it stands, and who sent
 * it when. Never a token, nor more of the account than its last four characters.
 */
export const billPaymentSchema = z.strictObject({
	fileId: z.string().min(1).max(128),
	vendorId: z.string().min(1).max(128),
	/** The vendor's name when it was sent. */
	vendorName: z.string().min(1).max(160),
	/** The account it was sent to, named as `bankAccountHint` names one: "IBAN ending 5678". */
	account: z.string().min(1).max(40),
	amount: filedAmountSchema,
	reference: z.string().min(1).max(BILL_PAYMENT_REFERENCE_MAX_LENGTH),
	status: z.enum(BILL_PAYMENT_STATUSES),
	/** When it was sent, as an ISO 8601 timestamp in UTC, and the name of the member who sent it. */
	sentAt: z.iso.datetime(),
	sentBy: z.string().min(1).max(80),
	/** When it was last sent to Revolut: its first sending, or the last time it was sent again. */
	lastSentAt: z.iso.datetime(),
	/** When Revolut last said where it stands. */
	checkedAt: z.iso.datetime().optional(),
	/** Why Revolut refused it, or the transfer failed, in Revolut's words. */
	failedReason: z.string().min(1).max(300).optional(),
});
export type BillPayment = z.infer<typeof billPaymentSchema>;

/** A vendor a bill is linked to, which it may be paid to: with its account's last four characters, or none yet. */
export const billPaymentVendorSchema = z.strictObject({
	vendorId: z.string().min(1).max(128),
	name: z.string().min(1).max(160),
	account: z.string().min(1).max(40).optional(),
});
export type BillPaymentVendor = z.infer<typeof billPaymentVendorSchema>;

/** Where Revolut payments stand, as `GET /api/revolut/payments` says it — never who allowed them, or when. */
const BILL_PAYMENTS_STATUSES = [
	"unavailable",
	"not-connected",
	"not-allowed",
	"allowed",
	"reconnect",
] as const satisfies readonly RevolutPayments["status"][];

/**
 * What `GET /api/revolut/bill-payments/{fileId}` and `POST /api/revolut/bill-payments` answer: whether Revolut payments
 * are allowed — not who allowed them, which only owners and admins see — the vendors the bill is linked to, A→Z, its
 * draft payment, or null while it has none, and whether Ravnar can still ask Revolut about that draft (`followed`):
 * payments are allowed, and are the ones it was sent under. False while it has none.
 */
export const billPaymentAnswerSchema = z.strictObject({
	payments: z.strictObject({ status: z.enum(BILL_PAYMENTS_STATUSES) }),
	vendors: z.array(billPaymentVendorSchema),
	billPayment: billPaymentSchema.nullable(),
	followed: z.boolean(),
});
export type BillPaymentAnswer = z.infer<typeof billPaymentAnswerSchema>;

/** What `POST /api/revolut/bill-payments` takes: the bill, the vendor it is paid to, and the transfer's reference. */
export const billPaymentRequestSchema = z.strictObject({
	fileId: z.string().min(1).max(128),
	vendorId: z.string().min(1).max(128),
	reference: z
		.string()
		.trim()
		.min(1, "Enter a reference")
		.max(BILL_PAYMENT_REFERENCE_MAX_LENGTH, "Use at most 100 characters"),
});
export type BillPaymentRequest = z.infer<typeof billPaymentRequestSchema>;

/**
 * What `DELETE /api/revolut/bill-payments/{fileId}` takes: nothing, to delete a draft in Revolut, or `forget=true`, to
 * forget one Ravnar cannot follow (`canForgetBillPayment`).
 */
export const billPaymentDeleteQuerySchema = z.strictObject({ forget: z.literal("true").optional() });

/** What an audit entry names a bill's draft payment by. */
type AuditedBillPayment = Pick<BillPayment, "amount" | "vendorName" | "account">;

/**
 * "Bill sent to Revolut" / "invoice-vantage-cloud-annual.pdf · 2880.00 EUR · Vantage Cloud · IBAN ending 5678": the
 * bill, what it comes to, and where it is to be paid, by the account's last four characters alone.
 */
export function billSentToRevolutAudit(fileName: string, payment: AuditedBillPayment): FinanceAuditEvent {
	const { amount, vendorName, account } = payment;
	return {
		action: "Bill sent to Revolut",
		detail: `${fileName} · ${amount.amount} ${amount.currency} · ${vendorName} · ${account}`,
	};
}

/**
 * "Bill refused by Revolut" / "invoice-vantage-cloud-annual.pdf · 2880.00 EUR · Vantage Cloud · IBAN ending 5678": a
 * bill whose draft Revolut would not take the first time it was sent, named as one sent is. What Revolut said is kept on
 * the bill, never here: its words may hold the whole account number.
 */
export function billRefusedByRevolutAudit(fileName: string, payment: AuditedBillPayment): FinanceAuditEvent {
	const { amount, vendorName, account } = payment;
	return {
		action: "Bill refused by Revolut",
		detail: `${fileName} · ${amount.amount} ${amount.currency} · ${vendorName} · ${account}`,
	};
}

/** "Draft payment deleted" / "invoice-vantage-cloud-annual.pdf · 2880.00 EUR · Vantage Cloud". */
export function draftPaymentDeletedAudit(fileName: string, payment: AuditedBillPayment): FinanceAuditEvent {
	const { amount, vendorName } = payment;
	return {
		action: "Draft payment deleted",
		detail: `${fileName} · ${amount.amount} ${amount.currency} · ${vendorName}`,
	};
}

/** "Draft payment forgotten" / "invoice-vantage-cloud-annual.pdf · 2880.00 EUR · Vantage Cloud". */
export function draftPaymentForgottenAudit(fileName: string, payment: AuditedBillPayment): FinanceAuditEvent {
	const { amount, vendorName } = payment;
	return {
		action: "Draft payment forgotten",
		detail: `${fileName} · ${amount.amount} ${amount.currency} · ${vendorName}`,
	};
}
